import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from '@/context/AuthContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { AppShell } from '@/components/AppShell';
import { usePageView } from '@/hooks/usePageView';

// Pages
import { HomePage } from '@/pages/HomePage';
import { ApplyPage } from '@/pages/ApplyPage';
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { ProjectsPage } from '@/pages/ProjectsPage';
import { AboutPage } from '@/pages/AboutPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { BlogListPage } from '@/pages/BlogListPage';
import { BlogPostPage } from '@/pages/BlogPostPage';
import { BlogManager } from '@/pages/BlogManager';
import { PageEditor } from '@/pages/PageEditor';
import { LeadsManager } from '@/pages/LeadsManager';
import { TrafficDashboard } from '@/pages/TrafficDashboard';
import { FounderPage } from '@/pages/FounderPage';
import { TermsPage } from '@/pages/TermsPage';

class ErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean, error: Error | null}> {
  state = { hasError: false, error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return <div className="p-8 text-red-600"><h1>Something went wrong</h1><pre>{this.state.error?.toString()}</pre></div>;
    }
    return this.props.children;
  }
}

function AppRoutes() {
  usePageView();
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/apply" element={<ApplyPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/founder" element={<FounderPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/blog" element={<BlogListPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/wizard" element={<Navigate to="/apply" replace />} />
      <Route path="/login" element={<LoginPage />} />

      {/* Protected routes */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AppShell>
              <Routes>
                <Route path="/dashboard" element={<TrafficDashboard />} />
                <Route path="/projects" element={<ProjectsPage />} />
                <Route path="/cms/leads" element={<LeadsManager />} />
                <Route path="/cms/blog" element={<BlogManager />} />
                <Route path="/cms/pages" element={<PageEditor />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </AppShell>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
